/**
 * The Divider component is used to delineate sections of the page. Commonly gray inside page content, it can also be a multi-colored bar, as used in various footers for both desktop and mobile. Margin and bottom padding values can also be passed as props for specific spacing.
 *
 * @module views/Design/Divider
 */
import './Divider.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Divider as PalDivider } from 'web-palette/dist/components/Divider/Divider';

import { spreadProps } from '@ulta/core/utils/dom/dom';

/**
 * Represents a Divider component
 *
 * @method
 * @param {DividerProps} props - React properties passed from composition
 * @returns Divider
 */
export const Divider = function( props ){
  const {
    type = defaultProps.type,
    color,
    direction = defaultProps.variant,
    className
  } = props;

  const formattedColor = color || paletteColorMapping[`${type}`];

  return (
    <div
      className={ classNames( 'Divider', {
        [ `Divider__${direction}` ]: direction
      } ) }
    >
      <PalDivider
        direction={ direction }
        className={ className }
        { ...spreadProps( {
          color: formattedColor
        } ) }
      />
    </div>
  );
};

/**
 * Palette color mapping used to translate component `type` values
 * into palette color tokens passed to the underlying PalDivider.
 *
 * @typedef PaletteColorMapping
 * @type {object}
 * @property {string} gray - Maps the 'gray' divider type to the 'xsubtle' palette color
 */
export const paletteColorMapping = {
  gray: 'xsubtle'
};

/**
 * property type definitions
 * @typedef DividerProps
 * @type {object}
 * @property {object} propTypes
 * @property {string} type - Sets divider type
 * @property {string} [color] - Overrides the palette color (optional)
 * @property {string} direction - Sets divider orientation ('horizontal' or 'vertical')
 * @property {string} [className] - Additional class names for custom styling
 */
export const propTypes =  {
  type: PropTypes.string,
  color: PropTypes.string,
  direction: PropTypes.string,
  className: PropTypes.string
};

/**
 * Default values for passed properties
 * @property { object } defaultProps
 * @property { string } [type='gray'] defaultProps.type - Set to 'gray' for displaying the default divider
 * @property { string } [direction='horizontal'] defaultProps.direction - Sets the divider orientation ('horizontal' or 'vertical')
 */
export const defaultProps =  {
  type: 'gray',
  direction: 'horizontal'
};

Divider.propTypes = propTypes;
Divider.defaultProps = defaultProps;

export default Divider;
